import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { HistoryResponse } from "@/components/HistoryDrawer";
import { api } from "@/lib/api/client";
import { unwrap } from "@/lib/api/unwrap";

import type {
  Blocker,
  CatalogueItem,
  CatalogueKind,
  CatalogueWrite,
  FlagKey,
  FlagRequest,
  Impact,
} from "./types";

export const catalogueKeys = {
  all: ["catalogue"] as const,
  list: (kind: CatalogueKind) => ["catalogue", "list", kind] as const,
  flags: (flag: FlagKey) => ["catalogue", "flags", flag] as const,
  history: (kind: CatalogueKind, id: string) =>
    ["catalogue", "history", kind, id] as const,
  recent: (kind: CatalogueKind) => ["catalogue", "recent", kind] as const,
  blockers: (kind: CatalogueKind, id: string) =>
    ["catalogue", "blockers", kind, id] as const,
  sizes: ["catalogue", "company-sizes"] as const,
  icons: ["catalogue", "icons"] as const,
  palette: (colour: string) => ["catalogue", "palette", colour] as const,
};

/**
 * A refused catalogue write, with what the server said about it.
 *
 * The generic ``unwrap`` keeps only ``detail`` and flat field errors; these
 * refusals carry structured bodies (the impact to confirm, the blockers, the
 * form errors) that the screens need intact.
 */
export class CatalogueWriteError extends Error {
  status: number;
  code?: string;
  impact?: Impact;
  blockers?: Blocker[];
  errors?: Record<string, string[]>;
  detail?: string;

  constructor(status: number, body: unknown) {
    super(`catalogue write refused (${status})`);
    this.name = "CatalogueWriteError";
    this.status = status;
    if (body && typeof body === "object") {
      const obj = body as Record<string, unknown>;
      if (typeof obj.code === "string") this.code = obj.code;
      if (typeof obj.detail === "string") this.detail = obj.detail;
      if (obj.impact && typeof obj.impact === "object") {
        this.impact = obj.impact as Impact;
      }
      if (Array.isArray(obj.blockers)) this.blockers = obj.blockers as Blocker[];
      if (obj.errors && typeof obj.errors === "object") {
        this.errors = obj.errors as Record<string, string[]>;
      }
    }
  }
}

async function written<T>(
  promise: Promise<{ data?: T; error?: unknown; response: Response }>,
): Promise<T> {
  const { data, error, response } = await promise;
  if (error !== undefined || !response.ok) {
    throw new CatalogueWriteError(response.status, error);
  }
  return data as T;
}

export function useCatalogueList(kind: CatalogueKind, enabled = true) {
  return useQuery({
    queryKey: catalogueKeys.list(kind),
    enabled,
    queryFn: async () => {
      const data = await unwrap(
        api.GET("/api/v1/catalogue/{kind}/", { params: { path: { kind } } }),
      );
      return data.items as CatalogueItem[];
    },
  });
}

export function useCompanySizes() {
  return useQuery({
    queryKey: catalogueKeys.sizes,
    queryFn: () => unwrap(api.GET("/api/v1/questionnaire/company-sizes/", {})),
  });
}

/** The free Font Awesome icons an issue can wear; empty when the server cannot read them. */
export function useIcons() {
  return useQuery({
    queryKey: catalogueKeys.icons,
    queryFn: () => unwrap(api.GET("/api/v1/catalogue/icons/", {})),
    staleTime: Infinity,
  });
}

/** The colours derived from a custom thematic colour, for the picker's preview. */
export function usePalettePreview(colour: string | null) {
  return useQuery({
    queryKey: catalogueKeys.palette(colour ?? ""),
    queryFn: () =>
      unwrap(
        api.GET("/api/v1/catalogue/palette/", {
          params: { query: { colour: colour ?? "" } },
        }),
      ),
    enabled: colour !== null,
    staleTime: Infinity,
  });
}

/** Create (``id`` null) or partially update a row; refusals are thrown intact. */
export function useSaveItem(kind: CatalogueKind) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string | null; body: CatalogueWrite }) =>
      id
        ? written(
            api.PATCH("/api/v1/catalogue/{kind}/{id}/", {
              params: { path: { kind, id } },
              body,
            }),
          )
        : written(
            api.POST("/api/v1/catalogue/{kind}/", {
              params: { path: { kind } },
              body,
            }),
          ),
    // Every kind can change what another lists (a renamed issue is a goal's
    // breadcrumb, a sector's actions are an action's sectors): refetch them all.
    onSettled: () => qc.invalidateQueries({ queryKey: catalogueKeys.all }),
  });
}

export function useDeletionBlockers(kind: CatalogueKind, id: string | null) {
  return useQuery({
    queryKey: catalogueKeys.blockers(kind, id ?? ""),
    enabled: Boolean(id),
    staleTime: 0,
    queryFn: async () => {
      const data = await unwrap(
        api.GET("/api/v1/catalogue/{kind}/{id}/deletion-blockers/", {
          params: { path: { kind, id: id as string } },
        }),
      );
      return data.blockers as Blocker[];
    },
  });
}

export function useDeleteItem(kind: CatalogueKind) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      written(
        api.DELETE("/api/v1/catalogue/{kind}/{id}/", {
          params: { path: { kind, id } },
        }),
      ),
    onSettled: () => qc.invalidateQueries({ queryKey: catalogueKeys.all }),
  });
}

export function useMoveItem(kind: CatalogueKind) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, direction }: { id: string; direction: "up" | "down" }) =>
      unwrap(
        api.POST("/api/v1/catalogue/{kind}/{id}/move/", {
          params: { path: { kind, id } },
          body: { direction },
        }),
      ),
    onSettled: () => qc.invalidateQueries({ queryKey: catalogueKeys.all }),
  });
}

export function useFlagRequests(flag: FlagKey) {
  return useQuery({
    queryKey: catalogueKeys.flags(flag),
    queryFn: async () => {
      const data = await unwrap(
        api.GET("/api/v1/catalogue/flags/{flag}/", {
          params: { path: { flag } },
        }),
      );
      return data.requests as FlagRequest[];
    },
  });
}

/** Propose a flag value: never applied, staged for a second administrator. */
export function useStageFlag(flag: FlagKey) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ targetId, value }: { targetId: string; value: boolean }) =>
      unwrap(
        api.POST("/api/v1/catalogue/flags/{flag}/stage/", {
          params: { path: { flag } },
          body: { target_id: targetId, value },
        }),
      ),
    onSettled: () => qc.invalidateQueries({ queryKey: catalogueKeys.all }),
  });
}

export interface FlagDecision {
  decision: "approve" | "propose";
  request_ids: string[];
  values?: Record<string, boolean>;
  note?: string;
}

export function useReviewFlag(flag: FlagKey) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (decision: FlagDecision) =>
      written(
        api.POST("/api/v1/catalogue/flags/{flag}/review/", {
          params: { path: { flag } },
          body: decision,
        }),
      ),
    onSettled: () => qc.invalidateQueries({ queryKey: catalogueKeys.all }),
  });
}

export function useItemHistory(kind: CatalogueKind, id: string | null) {
  return useQuery({
    queryKey: catalogueKeys.history(kind, id ?? ""),
    enabled: Boolean(id),
    staleTime: 0,
    queryFn: async () =>
      (await unwrap(
        api.GET("/api/v1/catalogue/history/{kind}/{id}/", {
          params: { path: { kind, id: id as string } },
        }),
      )) as unknown as HistoryResponse,
  });
}

export function useRecentHistory(kind: CatalogueKind, enabled: boolean) {
  return useQuery({
    queryKey: catalogueKeys.recent(kind),
    enabled,
    staleTime: 0,
    queryFn: async () =>
      (await unwrap(
        api.GET("/api/v1/catalogue/history/{kind}/", {
          params: { path: { kind } },
        }),
      )) as unknown as HistoryResponse,
  });
}

/** What undoing a deletion needs (``required``) and loses (``warnings``). */
export interface RestoreCheck {
  item: { id: string; name: string; crumb: string } | null;
  required: {
    field: string;
    label: string;
    missing: string;
    options: { id: string; name: string; crumb: string }[];
    chosen: string;
  }[];
  warnings: { label: string; count: number | null }[];
}

/** The answer to a restore request: done, or what still has to be decided. */
export type RestoreOutcome =
  | { status: "restored" }
  | { status: "ask"; check: RestoreCheck }
  | { status: "refused"; code: string };

function outcome(response: Response, body: unknown): RestoreOutcome {
  const obj = (body ?? {}) as { code?: string; check?: RestoreCheck };
  if (response.status === 201) return { status: "restored" };
  if (response.status === 200) return { status: "ask", check: body as RestoreCheck };
  if (response.status === 409 && obj.check) return { status: "ask", check: obj.check };
  return { status: "refused", code: obj.code ?? "error" };
}

export async function checkRestore(logId: string): Promise<RestoreOutcome> {
  const { data, error, response } = await api.GET("/api/v1/catalogue/restore/{log_id}/", {
    params: { path: { log_id: logId } },
  });
  return outcome(response, data ?? error);
}

/** Undo a deletion; on success every catalogue list and history is refetched. */
export function useRestore() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      logId,
      choices,
      acceptWarnings,
    }: {
      logId: string;
      choices?: Record<string, string>;
      acceptWarnings?: boolean;
    }) => {
      const { data, error, response } = await api.POST("/api/v1/catalogue/restore/{log_id}/", {
        params: { path: { log_id: logId } },
        body: { choices: choices ?? {}, accept_warnings: acceptWarnings ?? false },
      });
      return outcome(response, data ?? error);
    },
    onSuccess: (result) => {
      if (result.status === "restored") void qc.invalidateQueries({ queryKey: catalogueKeys.all });
    },
  });
}
